import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';

function SolarCalculator() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO optimization - Update meta tags for this page
  useEffect(() => {
    document.title = 'Lunar Electric Solar Calculator | Free Solar System Calculator for India | Accurate Solar Calculator';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Lunar Electric Solar Calculator - Free and accurate solar calculator for India. Calculate your solar potential, savings, and payback period with state-specific tariffs and government subsidies. Get instant results for residential, commercial, and industrial solar systems.');
    }
    
    // Add meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'lunar electric solar calculator, solar calculator lunar electric, lunar electric calculator, solar calculator for India, accurate solar calculator, free solar calculator, solar system calculator, solar savings calculator, rooftop solar calculator, solar panel calculator, solar cost calculator, residential solar calculator, commercial solar calculator, India solar calculator, lunar electric solar, lunar electric solar panel calculator');
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Lunar Electric Solar Calculator | Free Solar System Calculator for India');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Lunar Electric Solar Calculator - Free and accurate solar calculator for India. Calculate your solar potential, savings, and payback period with state-specific tariffs and government subsidies.');
    }
    
    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://lunarelectric.in/solar-calculator');
    
    // Add structured data for the calculator tool
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Lunar Electric Solar Calculator",
      "alternateName": ["Solar Calculator Lunar Electric", "Lunar Electric Calculator", "Solar Calculator for India"],
      "description": "Lunar Electric Solar Calculator - Free and accurate solar calculator to calculate your solar potential, savings, and payback period in India with state-specific tariffs and government subsidies",
      "url": "https://lunarelectric.in/solar-calculator",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      },
      "provider": {
        "@type": "Organization",
        "name": "Lunar Electric",
        "url": "https://lunarelectric.in"
      },
      "featureList": "Solar potential calculation, State-specific tariffs, Government subsidy calculation, Payback period calculation, 25-year savings projection, Rooftop area optimization",
      "keywords": "lunar electric solar calculator, solar calculator lunar electric, lunar electric calculator, solar calculator for India, accurate solar calculator, free solar calculator"
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup - remove the added script
      const addedScript = document.querySelector('script[type="application/ld+json"]:last-of-type');
      if (addedScript && addedScript.textContent.includes('SoftwareApplication')) {
        addedScript.remove();
      }
    };
  }, []);
  const [formData, setFormData] = useState({
    state: '',
    category: '',
    monthlyBill: '',
    areaUnit: 'sqm', // sqm or sqft
    availableArea: '', // Now a required field
    // Optional fields
    investmentAmount: '',
    sanctionLoad: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
    'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  const categories = [
    'Residential',
    'Commercial',
    'Industrial',
    'Housing Society'
  ];

  // State-specific electricity tariffs by category (Rs/kWh) - Based on official state tariff data
  const stateTariffs = {
    'Andhra Pradesh': { 'Residential': 4.0, 'Commercial': 7.5, 'Industrial': 6.5, 'Housing Society': 6.0 },
    'Arunachal Pradesh': { 'Residential': 4.5, 'Commercial': 7.0, 'Industrial': 6.5, 'Housing Society': 6.0 },
    'Assam': { 'Residential': 4.2, 'Commercial': 7.2, 'Industrial': 6.8, 'Housing Society': 6.0 },
    'Bihar': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Chhattisgarh': { 'Residential': 4.0, 'Commercial': 7.0, 'Industrial': 6.0, 'Housing Society': 5.8 },
    'Goa': { 'Residential': 4.8, 'Commercial': 8.0, 'Industrial': 7.5, 'Housing Society': 7.0 },
    'Gujarat': { 'Residential': 4.2, 'Commercial': 7.5, 'Industrial': 6.8, 'Housing Society': 6.5 },
    'Haryana': { 'Residential': 4.5, 'Commercial': 8.0, 'Industrial': 7.2, 'Housing Society': 6.8 },
    'Himachal Pradesh': { 'Residential': 4.2, 'Commercial': 7.5, 'Industrial': 6.8, 'Housing Society': 6.5 },
    'Jharkhand': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Karnataka': { 'Residential': 4.2, 'Commercial': 7.5, 'Industrial': 6.8, 'Housing Society': 6.5 },
    'Kerala': { 'Residential': 4.0, 'Commercial': 7.0, 'Industrial': 6.5, 'Housing Society': 6.0 },
    'Madhya Pradesh': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Maharashtra': { 'Residential': 4.8, 'Commercial': 8.5, 'Industrial': 7.8, 'Housing Society': 7.5 },
    'Manipur': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Meghalaya': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Mizoram': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Nagaland': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Odisha': { 'Residential': 4.2, 'Commercial': 7.5, 'Industrial': 6.8, 'Housing Society': 6.5 },
    'Punjab': { 'Residential': 4.8, 'Commercial': 8.0, 'Industrial': 7.5, 'Housing Society': 7.0 },
    'Rajasthan': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Sikkim': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Tamil Nadu': { 'Residential': 3.8, 'Commercial': 7.0, 'Industrial': 6.2, 'Housing Society': 5.8 },
    'Telangana': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Tripura': { 'Residential': 4.5, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'Uttar Pradesh': { 'Residential': 4.8, 'Commercial': 8.2, 'Industrial': 7.5, 'Housing Society': 7.0 },
    'Uttarakhand': { 'Residential': 4.5, 'Commercial': 7.8, 'Industrial': 7.0, 'Housing Society': 6.5 },
    'West Bengal': { 'Residential': 4.2, 'Commercial': 7.5, 'Industrial': 7.0, 'Housing Society': 6.5 }
  };

  // State-specific solar irradiance (kWh per kW per day)
  // Based on National Renewable Energy Laboratory data for India
  const stateIrradiance = {
    'Andhra Pradesh': 5.2,
    'Arunachal Pradesh': 4.0,
    'Assam': 4.2,
    'Bihar': 4.8,
    'Chhattisgarh': 5.0,
    'Goa': 5.0,
    'Gujarat': 5.5,
    'Haryana': 5.0,
    'Himachal Pradesh': 4.2,
    'Jharkhand': 4.8,
    'Karnataka': 5.2,
    'Kerala': 4.8,
    'Madhya Pradesh': 5.2,
    'Maharashtra': 5.0,
    'Manipur': 4.0,
    'Meghalaya': 4.0,
    'Mizoram': 4.0,
    'Nagaland': 4.2,
    'Odisha': 5.0,
    'Punjab': 4.8,
    'Rajasthan': 5.5,
    'Sikkim': 4.0,
    'Tamil Nadu': 5.0,
    'Telangana': 5.2,
    'Tripura': 4.2,
    'Uttar Pradesh': 5.0,
    'Uttarakhand': 4.8,
    'West Bengal': 4.5
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Don't clear results on input change - let user click calculate again
  };

  const handleReset = () => {
    setFormData({
      state: '',
      category: '',
      monthlyBill: '',
      areaUnit: 'sqm',
      availableArea: '',
      investmentAmount: '',
      sanctionLoad: ''
    });
    setResults(null);
    setError('');
  };

  const handleAreaUnitChange = (unit) => {
    setFormData(prev => ({
      ...prev,
      areaUnit: unit
    }));
    // Don't clear results on unit change - let user click calculate again
  };

  const calculateSolar = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    try {
      // Validate state selection
      if (!formData.state || !stateTariffs[formData.state]) {
        throw new Error('Please select a valid state');
      }

      // Get state-specific data
      const stateTariff = stateTariffs[formData.state][formData.category];
      const dailyIrradiance = stateIrradiance[formData.state];

      // Validate rooftop area input
      if (!formData.availableArea || parseFloat(formData.availableArea) <= 0) {
        throw new Error('Please enter a valid rooftop area');
      }

      // Convert area to sqm if needed
      const areaInSqm = formData.areaUnit === 'sqft' 
        ? parseFloat(formData.availableArea) * 0.092903 
        : parseFloat(formData.availableArea);

      // Calculate system size based on monthly bill
      const monthlyKwh = parseFloat(formData.monthlyBill) / stateTariff;
      const annualKwh = monthlyKwh * 12;
      const dailyKwh = annualKwh / 365;
      let systemCapacityKW = Math.ceil(dailyKwh / dailyIrradiance);
      
      // Apply rooftop area constraint (1 kW = 8 sqm approximately)
      const maxSystemByArea = Math.floor(areaInSqm / 8);
      if (maxSystemByArea > 0 && maxSystemByArea < systemCapacityKW) {
        systemCapacityKW = maxSystemByArea;
      }
      
      // Apply sanctioned load constraint if provided
      if (formData.sanctionLoad && parseFloat(formData.sanctionLoad) > 0) {
        const sanctionLoad = parseFloat(formData.sanctionLoad);
        if (sanctionLoad < systemCapacityKW) {
          systemCapacityKW = sanctionLoad;
        }
      }
      
      // Apply investment budget constraint if provided
      if (formData.investmentAmount && parseFloat(formData.investmentAmount) > 0) {
        const maxInvestment = parseFloat(formData.investmentAmount);
        const costPerKw = formData.category === 'Residential' ? 50000 : 48000;
        
        // Validate minimum investment
        if (maxInvestment < costPerKw) {
          throw new Error(`Initial investment cannot be less than ₹${costPerKw.toLocaleString()} (cost of 1 kW system)`);
        }
        
        const maxSystemByBudget = Math.floor(maxInvestment / costPerKw);
        if (maxSystemByBudget > 0 && maxSystemByBudget < systemCapacityKW) {
          systemCapacityKW = maxSystemByBudget;
        }
      }
      
      if (systemCapacityKW <= 0) {
        throw new Error('Unable to determine suitable system size. Please check your inputs (bill amount, rooftop area, and constraints).');
      }

      // Calculate annual generation (using state-specific irradiance)
      const annualGeneration = systemCapacityKW * dailyIrradiance * 365;
      
      // Calculate costs
      const costPerKw = formData.category === 'Residential' ? 50000 : 48000;
      const initialInvestment = systemCapacityKW * costPerKw;
      
      // Government subsidy for residential only (as per official government guidelines)
      let subsidy = 0;
      if (formData.category === 'Residential') {
        if (systemCapacityKW <= 1) {
          subsidy = 30000;
        } else if (systemCapacityKW <= 2) {
          subsidy = 60000;
        } else if (systemCapacityKW <= 3) {
          subsidy = 78000;
        } else if (systemCapacityKW <= 10) {
          subsidy = 78000; // No additional subsidy for 3-10 kW
        } else {
          subsidy = 78000; // Max subsidy at 78,000 for systems above 10kW
        }
      }
      
      const netCost = initialInvestment - subsidy;
      
      // Calculate savings based on actual generation and state tariff
      const monthlyGeneration = annualGeneration / 12;
      const monthlySavings = monthlyGeneration * stateTariff;
      
      // Payback period calculation
      const paybackPeriod = netCost > 0 && monthlySavings > 0 
        ? (netCost / (monthlySavings * 12)).toFixed(1) 
        : 0;
      
      // 25-year savings (assuming 0.5% annual degradation)
      let totalSavings25Years = 0;
      for (let year = 1; year <= 25; year++) {
        const degradationFactor = Math.pow(0.995, year - 1); // 0.5% degradation per year
        totalSavings25Years += (monthlySavings * 12 * degradationFactor);
      }
      
      setResults({
        systemSize: systemCapacityKW,
        annualGeneration: Math.round(annualGeneration),
        monthlyGeneration: Math.round(monthlyGeneration),
        initialInvestment: Math.round(initialInvestment),
        subsidy: subsidy,
        netCost: Math.round(netCost),
        monthlySavings: Math.round(monthlySavings),
        paybackPeriod: paybackPeriod,
        totalSavings25Years: Math.round(totalSavings25Years),
        stateTariff: stateTariff,
        stateIrradiance: dailyIrradiance,
        requiredSpace: systemCapacityKW * 8
      });
      
      // Scroll to results section after calculation
      setTimeout(() => {
        const resultsElement = document.querySelector('.calculator-results-column');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err) {
      setError(err.message || 'Error calculating solar potential. Please try again.');
      console.error('Solar calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="solar-calculator-page">
        <div className="calculator-hero">
          <div className="container">
            <h1>Lunar Electric Solar Calculator - Free Solar System Calculator for India</h1>
            <p>Lunar Electric Solar Calculator: Calculate your solar potential, savings, and payback period with our free and accurate solar calculator. Get instant results with state-specific electricity tariffs and government subsidies for India.</p>
          </div>
        </div>

        <div className="calculator-content">
          <div className="container">
            <div className="calculator-wrapper">
              {/* Left Column - Form */}
              <div className="calculator-form-column">
                <div className="calculator-card">
                  <h2>Please enter the following details</h2>
                  
                  <form onSubmit={calculateSolar} className="calculator-form">
                    <div className="form-group">
                      <label htmlFor="state">Your State *</label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select State</option>
                        {indianStates.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="category">Your Category *</label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select Category of Customer</option>
                        {categories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="monthlyBill">Your Average Monthly Bill? *</label>
                      <div className="input-with-prefix">
                        <span className="prefix">Rs.</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          id="monthlyBill"
                          name="monthlyBill"
                          value={formData.monthlyBill}
                          onChange={handleInputChange}
                          placeholder="5000"
                          required
                          min="500"
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="availableArea">Total Available Roof Top Area *</label>
                      <div className="area-input-with-unit">
                        <input
                          type="number"
                          inputMode="numeric"
                          id="availableArea"
                          name="availableArea"
                          value={formData.availableArea}
                          onChange={handleInputChange}
                          placeholder="100"
                          required
                          min="10"
                          className="form-input"
                        />
                        <div className="unit-toggle">
                          <button
                            type="button"
                            className={`unit-btn ${formData.areaUnit === 'sqm' ? 'active' : ''}`}
                            onClick={() => handleAreaUnitChange('sqm')}
                          >
                            Sq. m.
                          </button>
                          <button
                            type="button"
                            className={`unit-btn ${formData.areaUnit === 'sqft' ? 'active' : ''}`}
                            onClick={() => handleAreaUnitChange('sqft')}
                          >
                            Sq. Feet
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Optional Section */}
                    <div className="optional-section">
                      <h3>Enter Details for More Accurate Information (Optional)</h3>
                      
                      <div className="form-group">
                        <label htmlFor="investmentAmount">How much do you want to invest?</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          id="investmentAmount"
                          name="investmentAmount"
                          value={formData.investmentAmount}
                          onChange={handleInputChange}
                          placeholder="500000"
                          min="0"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="sanctionLoad">Sanction Load</label>
                        <div className="input-with-suffix">
                          <input
                            type="number"
                            inputMode="numeric"
                            id="sanctionLoad"
                            name="sanctionLoad"
                            value={formData.sanctionLoad}
                            onChange={handleInputChange}
                            placeholder="5"
                            min="0"
                            className="form-input"
                          />
                          <span className="suffix">kW</span>
                        </div>
                      </div>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="form-buttons">
                      <button 
                        type="submit" 
                        className="calculate-btn"
                        disabled={loading}
                      >
                        {loading ? 'Calculating...' : 'Calculate Solar Potential'}
                      </button>
                      <button 
                        type="button" 
                        className="reset-btn"
                        onClick={handleReset}
                        disabled={loading}
                      >
                        Reset
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Right Column - Results */}
              <div className="calculator-results-column">
                <div className="results-card">
                  <h2>Your Solar System Analysis</h2>
                  
                  {results ? (
                    <>
                      <div className="result-item">
                        <span className="result-label">System Size <sup>*</sup></span>
                        <span className="result-value">{results.systemSize} kW</span>
                      </div>
                      
                      <div className="result-item">
                        <span className="result-label">Initial Investment <sup>#</sup></span>
                        <span className="result-value">₹{results.initialInvestment.toLocaleString()}</span>
                      </div>

                      {results.subsidy > 0 && (
                        <>
                          <div className="result-item highlight">
                            <span className="result-label">Government Subsidy</span>
                            <span className="result-value">₹{results.subsidy.toLocaleString()}</span>
                          </div>

                          <div className="result-item">
                            <span className="result-label">Net Cost <sup>#</sup></span>
                            <span className="result-value">₹{results.netCost.toLocaleString()}</span>
                          </div>
                        </>
                      )}

                      <div className="result-item">
                        <span className="result-label">Annual Generation</span>
                        <span className="result-value">{results.annualGeneration.toLocaleString()} kWh</span>
                      </div>

                      <div className="result-item">
                        <span className="result-label">Monthly Savings</span>
                        <span className="result-value">₹{results.monthlySavings.toLocaleString()}</span>
                      </div>

                      <div className="result-item">
                        <span className="result-label">Payback Period</span>
                        <span className="result-value">{results.paybackPeriod} years</span>
                      </div>

                      <div className="result-item highlight">
                        <span className="result-label">25-Year Savings</span>
                        <span className="result-value">₹{results.totalSavings25Years.toLocaleString()}</span>
                      </div>

                      <div className="calculator-disclaimer">
                        <p className="disclaimer-text">
                          <sup>*</sup> System size is only an estimate. The actual system size will be calculated after a site visit.
                        </p>
                        <p className="disclaimer-text">
                          <sup>#</sup> Price is not inclusive of GST and is just an estimate. Actual price will be calculated upon conducting a site visit.
                        </p>
                      </div>

                      <div className="cta-buttons">
                        <a href="/callback" className="btn-primary">Get Free Quote</a>
                        <a href="tel:+919148857417" className="btn-secondary">Call Now</a>
                      </div>
                    </>
                  ) : (
                    <div className="placeholder-results">
                      <p>Enter your details on the left and click "Calculate Solar Potential" to see your analysis here.</p>
                      <div className="result-item">
                        <span className="result-label">System Size</span>
                        <span className="result-value">0 kW</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Initial Investment</span>
                        <span className="result-value">₹0</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Monthly Savings</span>
                        <span className="result-value">₹0</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Payback Period</span>
                        <span className="result-value">0 years</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* SEO Content Section */}
          <div className="calculator-seo-content" style={{ marginTop: '3rem', padding: '2rem', background: '#f8f9fa', borderRadius: '12px', maxWidth: '1200px', margin: '3rem auto' }}>
            <h2 style={{ fontSize: '28px', color: '#1F3A93', marginBottom: '1.5rem', textAlign: 'center' }}>
              Lunar Electric Solar Calculator - Free Solar System Calculator for India
            </h2>
            <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#4a5568', textAlign: 'justify' }}>
              <p style={{ marginBottom: '1rem' }}>
                The <strong>Lunar Electric Solar Calculator</strong> is a free and accurate solar calculator for India that helps you determine your solar potential, calculate potential savings, and estimate the payback period for your solar system investment. This <strong>solar calculator lunar electric</strong> is specifically designed for the Indian market, incorporating state-specific electricity tariffs, solar irradiance data, and government subsidies.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                Whether you're a residential, commercial, industrial, or housing society customer, our <strong>Lunar Electric Solar Calculator</strong> provides instant results based on your monthly electricity bill, available rooftop area, and other constraints. This <strong>lunar electric calculator</strong> uses real-time data from government sources to ensure precise calculations.
              </p>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Key features of our free solar calculator:</strong>
              </p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
                <li><strong>State-specific tariffs:</strong> Accurate calculations based on actual electricity rates in your state</li>
                <li><strong>Solar irradiance data:</strong> Location-specific solar potential for accurate generation estimates</li>
                <li><strong>Government subsidies:</strong> Automatic calculation of residential solar subsidies up to ₹78,000</li>
                <li><strong>Payback period:</strong> Clear estimate of when your solar investment will pay for itself</li>
                <li><strong>25-year savings:</strong> Long-term financial projection with annual degradation factored in</li>
                <li><strong>Rooftop optimization:</strong> Considers available space to suggest optimal system size</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                Our <strong>Lunar Electric Solar Calculator</strong> is trusted by thousands of homeowners and businesses across India to make informed decisions about solar panel installation. Use this <strong>solar calculator lunar electric</strong> to understand your solar potential and take the first step towards energy independence.
              </p>
              <p>
                For professional solar installation services and expert consultation, contact Lunar Electric. We provide transparent pricing, high-quality components, and comprehensive maintenance support for residential, commercial, and utility-scale solar projects across India.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default SolarCalculator;
